import { useEffect, useRef, useState } from "react";

/**
 * Measure the host element's width so SVG charts can render with the
 * viewBox set to the real pixel width — that way `preserveAspectRatio`
 * stays at its uniform default and text doesn't get horizontally squashed.
 *
 * Returns [ref, width]. `width` is 0 until the first measurement; render a
 * placeholder skeleton if you need something for that frame.
 */
export function useContainerWidth(): [
  React.RefObject<HTMLDivElement | null>,
  number,
] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(Math.round(el.getBoundingClientRect().width));
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setWidth(Math.round(w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, width];
}
